<template>
  <div class="password-reset-page d-flex align-items-center justify-content-center">
    <div class="password-reset-card">
      <div class="text-center mb-4">
        <img src="images/logo-white-shadow.svg" alt="Logo" class="password-reset-logo mb-2">
        <h1 class="password-reset-title">
          Réinitialiser le mot de passe
        </h1>
      </div>

      <output v-if="resetComplete" class="text-center d-block">
        <p class="password-reset-description">
          Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter.
        </p>
        <b-button variant="primary" block class="mt-4" @click="goToLogin">
          Aller à la connexion
        </b-button>
      </output>

      <div v-else-if="!token" class="text-center" role="alert">
        <p class="password-reset-description">
          Ce lien de réinitialisation est incomplet ou invalide.
        </p>
        <b-button variant="primary" block class="mt-4" @click="goToLogin">
          Retour à la connexion
        </b-button>
      </div>

      <form v-else @submit.prevent="handleSubmit">
        <p class="password-reset-description">
          Choisissez un nouveau mot de passe pour votre compte.
        </p>

        <b-form-group label-for="password-reset-password" class="password-reset-label">
          <template #label>
            Nouveau mot de passe <span class="required-marker" aria-hidden="true">*</span>
            <span class="sr-only">requis</span>
          </template>
          <b-form-input id="password-reset-password" v-model="password" type="password" autocomplete="new-password"
                        required autofocus :disabled="isSubmitting" @input="clearError" />
        </b-form-group>

        <ul class="password-requirements" aria-label="Exigences du mot de passe">
          <li :class="{ 'text-success': passwordRequirements.length }">
            Au moins 8 caractères
          </li>
          <li :class="{ 'text-success': passwordRequirements.uppercase }">
            Une lettre majuscule
          </li>
          <li :class="{ 'text-success': passwordRequirements.lowercase }">
            Une lettre minuscule
          </li>
          <li :class="{ 'text-success': passwordRequirements.digit }">
            Un chiffre
          </li>
          <li :class="{ 'text-success': passwordRequirements.special }">
            Un caractère spécial
          </li>
        </ul>

        <b-form-group label-for="password-reset-confirmation" class="password-reset-label">
          <template #label>
            Confirmation du mot de passe <span class="required-marker" aria-hidden="true">*</span>
            <span class="sr-only">requise</span>
          </template>
          <b-form-input id="password-reset-confirmation" v-model="confirmationPassword" type="password"
                        autocomplete="new-password" required :disabled="isSubmitting" @input="clearError" />
        </b-form-group>

        <div v-if="password && confirmationPassword && !passwordsMatch" class="password-mismatch" role="alert">
          Les mots de passe ne correspondent pas.
        </div>

        <div v-if="errorMessage" class="password-reset-error text-center" role="alert">
          {{ errorMessage }}
        </div>

        <b-button type="submit" variant="primary" block class="mt-4"
                  :disabled="isSubmitting || !isPasswordValid || !passwordsMatch">
          {{ isSubmitting ? 'Réinitialisation...' : 'Réinitialiser le mot de passe' }}
        </b-button>

        <b-button type="button" variant="link" block class="mt-2" :disabled="isSubmitting" @click="goToLogin">
          Retour à la connexion
        </b-button>
      </form>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axiosIns'
import {
  BButton,
  BFormGroup,
  BFormInput,
} from 'bootstrap-vue'

export default {
  name: 'PasswordReset',
  components: {
    BButton,
    BFormGroup,
    BFormInput,
  },
  data() {
    return {
      confirmationPassword: '',
      errorMessage: null,
      isSubmitting: false,
      password: '',
      resetComplete: false,
      token: null,
    }
  },
  computed: {
    passwordRequirements() {
      return {
        length: this.password.length >= 8,
        uppercase: /[A-Z]/.test(this.password),
        lowercase: /[a-z]/.test(this.password),
        digit: /\d/.test(this.password),
        special: /[^A-Za-z0-9]/.test(this.password),
      }
    },
    isPasswordValid() {
      return Object.values(this.passwordRequirements).every(Boolean)
    },
    passwordsMatch() {
      return this.password.length > 0
        && this.password === this.confirmationPassword
    },
  },
  created() {
    const hash = this.$route.hash.replace(/^#/, '')
    this.token = new URLSearchParams(hash).get('token')

    if (this.$route.hash) {
      this.$router.replace({ name: 'password-reset' }).catch(() => { })
    }
  },
  methods: {
    clearError() {
      this.errorMessage = null
    },
    goToLogin() {
      this.$router.replace({ name: 'login' })
    },
    async handleSubmit() {
      this.clearError()

      if (!this.token || !this.isPasswordValid || !this.passwordsMatch) {
        this.errorMessage = 'Choisissez un mot de passe conforme et confirmez-le.'
        return
      }

      this.isSubmitting = true

      try {
        await axios.post('Auth/CompletePasswordReset', {
          token: this.token,
          newPassword: this.password,
          confirmationPassword: this.confirmationPassword,
        })
        this.resetComplete = true
        this.password = ''
        this.confirmationPassword = ''
        this.token = null
      } catch (error) {
        const responseData = error.response && error.response.data
        if (error.response && error.response.status === 429) {
          this.errorMessage = 'Trop de tentatives. Veuillez patienter avant de réessayer.'
        } else if (responseData && responseData.error === 'The password reset request is invalid.') {
          this.errorMessage = 'Ce lien est invalide, expiré ou a déjà été utilisé.'
        } else if (responseData && responseData.errors) {
          this.errorMessage = this.getValidationMessage(responseData.errors)
        } else if (error.response) {
          this.errorMessage = "La réinitialisation n'a pas pu être effectuée. Veuillez réessayer."
        } else {
          this.errorMessage = "Une erreur réseau s'est produite. Vérifiez votre connexion."
        }
      } finally {
        this.isSubmitting = false
      }
    },
    getValidationMessage(errors) {
      const messages = Object.values(errors).reduce((allMessages, fieldErrors) => {
        if (Array.isArray(fieldErrors)) {
          return allMessages.concat(fieldErrors)
        }

        return allMessages.concat(String(fieldErrors))
      }, [])

      return messages[0] || 'Le mot de passe ne respecte pas les exigences de sécurité.'
    },
  },
}
</script>

<style scoped>
.password-reset-page {
  min-height: 100vh;
  padding: 1rem;
  color: white;
  background: #202329 url('/images/refuge.jpg') center / cover;
  position: relative;
}

.password-reset-page::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.62);
}

.password-reset-card {
  width: min(100%, 430px);
  padding: 2rem 3rem;
  border-radius: 10px;
  background: rgba(46, 46, 46, 0.9);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  position: relative;
}

.password-reset-logo {
  max-width: 180px;
}

.password-reset-title {
  font-size: 1.5rem;
  font-weight: bold;
}

.password-reset-description {
  color: #e5e5e5;
}

.password-reset-label label {
  color: white !important;
}

.password-requirements {
  padding-left: 1.25rem;
  color: #e5e5e5;
  font-size: 0.85rem;
}

.required-marker {
  color: #ff6b6b;
  font-weight: 800;
}

.password-mismatch,
.password-reset-error {
  color: #ffb3b3;
  font-size: 0.9rem;
  font-weight: 800;
}

@media (max-width: 480px) {
  .password-reset-card {
    padding: 1.5rem;
  }
}
</style>
